import styled from 'styled-components';
import { TFunction } from 'i18next';

import { ButtonS } from '../Button';
import { HydratedPosition } from '../../utils/data';
import {
  AlignCenterWrapper,
  AlignLeftWrapper,
  TextValue,
  MarketWrapper,
  MarketFlag,
  MarketLabel,
  Tooltip,
  CondensedRowContainer,
  CondensedRowHeader,
  CondensedValuesContainer,
  CondensedValue,
} from '../Table/components';
import { Label3 } from '../Text';
import { formatDollarValue } from '../../utils/general';

export const CloseButton = styled(ButtonS)`
  &:disabled {
    background: none;
    border-color: rgba(0, 0, 0, 0);

    &:disabled {
      box-shadow: none;
    }

    &:enabled:active {
      border-color: rgba(0, 0, 0, 0);
      box-shadow: none;
    }
  }
`;

export interface Row {
  id: string;
  market: React.ReactNode;
  direction: React.ReactNode;
  size: React.ReactNode;
  entryPrice: React.ReactNode;
  lastPrice: React.ReactNode;
  liquidationPrice: React.ReactNode;
  fundingPayments: React.ReactNode;
  pnL: React.ReactNode;
  action: React.ReactNode;
}

export const headers = (t: TFunction): Row => ({
  id: 'header',
  market: t('Market'),
  direction: t('Direction'),
  size: t('Size'),
  entryPrice: t('Entry price'),
  lastPrice: (
    <AlignLeftWrapper>
      {t('Last price')}
      <Tooltip size="s" position="bottom">
        {t('Last Price refers to the latest market price that the perpetuals was traded at.')}
      </Tooltip>
    </AlignLeftWrapper>
  ),
  liquidationPrice: t('Liquidation price'),
  fundingPayments: t('Funding payments'),
  pnL: (
    <AlignLeftWrapper>
      {t('Est. PnL')}
      <Tooltip size="s" position="bottom">
        {t('Calculated based on Index Price')}
      </Tooltip>
    </AlignLeftWrapper>
  ),
  action: <AlignCenterWrapper>{t('Action')}</AlignCenterWrapper>,
});

export const renderRow = (
  position: HydratedPosition,
  action: (position: HydratedPosition) => void,
  t: TFunction,
): Row => {
  const {
    id,
    name,
    flag,
    direction,
    size,
    entryPrice,
    lastPrice,
    liquidationPrice,
    fundingPayments,
    unrealizedPnl,
    closed,
  } = position;
  return {
    id,
    market: (
      <MarketWrapper closed={closed}>
        <MarketFlag src={flag} />
        <MarketLabel>{name}</MarketLabel>
      </MarketWrapper>
    ),
    direction: (
      <TextValue closed={closed} value={direction === 'Long' ? 1 : -1}>
        {t(direction)}
      </TextValue>
    ),
    size: <TextValue closed={closed}>{size}</TextValue>,
    entryPrice: <TextValue closed={closed}>{entryPrice}</TextValue>,
    lastPrice: <TextValue closed={closed}>{lastPrice}</TextValue>,
    liquidationPrice: <TextValue closed={closed}>{liquidationPrice ?? '-'}</TextValue>,
    fundingPayments: (
      <TextValue
        closed={closed}
        value={fundingPayments ? Number(fundingPayments.toString()) : null}
      >
        {position.fundingPayments ? formatDollarValue(position.fundingPayments, 18, false, 2) : '-'}
      </TextValue>
    ),
    pnL: (
      <TextValue
        closed={closed}
        value={position.unrealizedPnl ? Number(position.unrealizedPnl.toString()) : null}
      >
        {unrealizedPnl ? formatDollarValue(unrealizedPnl, 18, false, 2) : '-'}
      </TextValue>
    ),
    action: (
      <AlignCenterWrapper>
        <CloseButton disabled={closed} onClick={() => action(position)}>
          {closed ? t('Closed') : t('Close')}
        </CloseButton>
      </AlignCenterWrapper>
    ),
  };
};

export const renderCondensedRow = (
  position: HydratedPosition,
  action: (position: HydratedPosition) => void,
  t: TFunction,
) => {
  const {
    id,
    name,
    flag,
    direction,
    size,
    entryPrice,
    lastPrice,
    liquidationPrice,
    fundingPayments,
    unrealizedPnl,
    closed,
  } = position;
  return (
    <CondensedRowContainer key={id}>
      <CondensedRowHeader>
        <MarketWrapper closed={closed}>
          <MarketFlag src={flag} />
          <MarketLabel>{name}</MarketLabel>
        </MarketWrapper>
        <CloseButton disabled={closed} onClick={() => action(position)}>
          {closed ? t('Closed') : t('Close')}
        </CloseButton>
      </CondensedRowHeader>
      <CondensedValuesContainer>
        <CondensedValue>
          <Label3>{headers(t).direction}</Label3>
          <TextValue closed={closed} value={direction === 'Long' ? 1 : -1}>
            {t(direction)}
          </TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3>{headers(t).entryPrice}</Label3>
          <TextValue closed={closed}>{entryPrice}</TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3 as="div">{headers(t).lastPrice}</Label3>
          <TextValue closed={closed}>{lastPrice}</TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3>{headers(t).size}</Label3>
          <TextValue closed={closed}>{size}</TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3>{headers(t).liquidationPrice}</Label3>
          <TextValue closed={closed}>{liquidationPrice ?? '-'}</TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3>{headers(t).fundingPayments}</Label3>
          <TextValue
            closed={closed}
            value={fundingPayments ? Number(fundingPayments.toString()) : null}
          >
            {position.fundingPayments
              ? formatDollarValue(position.fundingPayments, 18, false, 2)
              : '-'}
          </TextValue>
        </CondensedValue>
        <CondensedValue>
          <Label3>{headers(t).pnL}</Label3>
          <TextValue
            closed={closed}
            value={position.unrealizedPnl ? Number(position.unrealizedPnl.toString()) : null}
          >
            {unrealizedPnl ? formatDollarValue(unrealizedPnl, 18, false, 2) : '-'}
          </TextValue>
        </CondensedValue>
      </CondensedValuesContainer>
    </CondensedRowContainer>
  );
};
